import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Info,
  TriangleAlert,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import { StudentPicker } from "@/components/shared/StudentPicker";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { OpsPersonCell } from "@/components/admin/ops-table";
import { cn } from "@/lib/utils";
import { useAdminStudents, useAdminPartners } from "@/hooks/useAdminData";
import { DatePicker } from "@/components/ui/date-picker";

export const Route = createFileRoute("/admin/placements/assign")({
  component: AdminPlacementAssignPage,
});

const STEPS = ["Select student", "Select partner & slot", "Set dates", "Confirm"];

function Stepper({ active }: { active: number }) {
  return (
    <ol className="grid grid-cols-4 gap-2" aria-label="Assignment steps">
      {STEPS.map((label, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <li
            key={label}
            aria-current={current ? "step" : undefined}
            className={cn(
              "flex min-w-0 flex-col gap-2 border-t-2 pt-2.5",
              done || current ? "border-foreground" : "border-border",
            )}
          >
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground tabular-nums">
              {done ? <Check className="size-3 text-foreground" aria-hidden /> : `0${i + 1}`}
              <span className="sr-only">{done ? "Completed" : current ? "Current" : "Upcoming"}</span>
            </span>
            <span
              className={cn(
                "truncate text-caption max-sm:hidden",
                current ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function SummaryItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="eyebrow mb-1.5">{label}</dt>
      <dd className="text-sm text-foreground">{children}</dd>
    </div>
  );
}

function AdminPlacementAssignPage() {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedPartner, setSelectedPartner] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { data: students = [] } = useAdminStudents();
  const { data: partners = [] } = useAdminPartners();

  const student: any = students.find(
    (s: any) => String(s.user_id ?? s.id) === String(selectedStudent),
  );
  const partner: any = partners.find((p: any) => String(p.id) === String(selectedPartner));

  if (done) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Placement assigned"
          breadcrumbs={[{ label: "Placements", path: "/admin/placements" }, { label: "Assign" }]}
        />

        <section className="rounded-md border border-border bg-card px-5 py-10 text-center md:px-10">
          <div className="mx-auto mb-5 flex size-10 items-center justify-center rounded-md border border-success/30 bg-success/10 text-success">
            <ClipboardCheck className="size-5" aria-hidden />
          </div>
          <h2 className="text-title text-foreground">Placement assigned successfully</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {student?.name} has been assigned to {partner?.name}
          </p>

          <dl className="mx-auto mt-6 grid max-w-lg gap-4 rounded-md border border-border bg-background p-5 text-left sm:grid-cols-2">
            <SummaryItem label="Student">{student?.name}</SummaryItem>
            <SummaryItem label="Partner">{partner?.name}</SummaryItem>
            <SummaryItem label="Start date">
              <span className="tabular-nums">{startDate || "Not set"}</span>
            </SummaryItem>
            <SummaryItem label="End date">
              <span className="tabular-nums">{endDate || "Not set"}</span>
            </SummaryItem>
          </dl>

          <div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row">
            <Button asChild>
              <Link to="/admin/placements">Back to placements</Link>
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setDone(false);
                setStep(0);
                setSelectedStudent("");
                setSelectedPartner("");
                setSelectedSlot("");
              }}
            >
              Assign another student
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assign student to partner"
        breadcrumbs={[{ label: "Placements", path: "/admin/placements" }, { label: "Assign" }]}
      />

      <section className="mx-auto max-w-3xl rounded-md border border-border bg-card">
        <div className="border-b border-border px-4 py-4 md:px-6">
          <Stepper active={step} />
        </div>

        <div className="px-4 py-6 md:px-6">
          {/* Step 0: Select student */}
          {step === 0 && (
            <div className="space-y-5">
              <h2 className="text-title text-foreground">Select student to assign</h2>
              <Field label="Select student">
                {({ id, describedBy }) => (
                  <StudentPicker
                    id={id}
                    aria-describedby={describedBy}
                    value={selectedStudent ? String(selectedStudent) : null}
                    onChange={(v) => setSelectedStudent(v ?? "")}
                    placeholder="Choose a student"
                    subtitle="Pick the student to place with a partner."
                    allGroupsLabel="All universities"
                    emptyText="No students available"
                    options={students
                      .filter((s: any) => s.status === "active" || s.status === "pending")
                      .map((s: any) => ({
                        value: String(s.user_id ?? s.id),
                        label: s.name,
                        description: `${s.university} — ${s.status}`,
                        group: s.university || undefined,
                      }))}
                  />
                )}
              </Field>
            </div>
          )}

          {/* Step 1: Select partner & slot */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-title text-foreground">Select partner organization</h2>
              <Field label="Select partner">
                {({ id, describedBy }) => (
                  <Combobox
                    id={id}
                    aria-describedby={describedBy}
                    value={selectedPartner ? String(selectedPartner) : null}
                    onChange={(v) => setSelectedPartner(v ?? "")}
                    placeholder="Choose a partner"
                    searchPlaceholder="Search partners…"
                    emptyText="No partners available"
                    options={partners
                      .filter((p: any) => p.status === "active")
                      .map((p: any) => ({
                        value: String(p.id),
                        label: p.name,
                        description: `${p.industry} — ${p.filled}/${p.slots} filled`,
                      }))}
                  />
                )}
              </Field>

              {selectedPartner && (
                <div className="flex items-center gap-3 rounded-md border border-border bg-background p-4">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border text-body">
                    <Building2 className="size-4" aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{partner?.name}</p>
                    <p className="truncate text-caption text-muted-foreground">
                      {partner?.industry} · {partner?.address}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Set dates */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-title text-foreground">Set placement duration</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Start date">
                  <DatePicker
                    value={startDate}
                    onChange={(v) => setStartDate(v)}
                  />
                </Field>
                <Field label="End date">
                  <DatePicker value={endDate} onChange={(v) => setEndDate(v)} />
                </Field>
              </div>
              <Alert variant="info">
                <Info />
                <AlertDescription>
                  The placement duration should align with the academic calendar and program
                  requirements.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Step 3: Confirm */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-title text-foreground">Review & confirm assignment</h2>
              <dl className="grid gap-5 rounded-md border border-border bg-background p-5 sm:grid-cols-2">
                <SummaryItem label="Student">
                  <OpsPersonCell name={student?.name ?? ""} secondary={student?.university} />
                </SummaryItem>
                <SummaryItem label="Partner">
                  <OpsPersonCell name={partner?.name ?? ""} secondary={partner?.industry} />
                </SummaryItem>
                {startDate && (
                  <SummaryItem label="Start date">
                    <span className="tabular-nums">{startDate}</span>
                  </SummaryItem>
                )}
                {endDate && (
                  <SummaryItem label="End date">
                    <span className="tabular-nums">{endDate}</span>
                  </SummaryItem>
                )}
              </dl>
              <Alert variant="warning">
                <TriangleAlert />
                <AlertDescription>
                  This action will notify both the student and partner organization via email.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 md:px-6">
          <Button
            variant="ghost"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
          >
            <ArrowLeft />
            Back
          </Button>

          {step < 3 ? (
            <Button
              onClick={() => setStep((s) => Math.min(3, s + 1))}
              disabled={
                (step === 0 && !selectedStudent) ||
                (step === 1 && !selectedPartner) ||
                (step === 2 && (!startDate || !endDate))
              }
            >
              Continue
              <ArrowRight />
            </Button>
          ) : (
            <Button onClick={() => setDone(true)}>
              <CheckCircle2 />
              Confirm & assign
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
